<template>
	<div class="container__crud">
		<div class="container__crud__sincronizacao">
			<img
				v-if="dadosParaSeremSincronizados.length"
				class="container__crud__sincronizacao__icon"
				src="../../../assets/sync_integracoes_icon.svg"
			/>
			<img
				v-else
				class="container__crud__sincronizacao__icon"
				src="../../../assets/sync_integracoes_icon_ok.svg"
			/>

			<label class="container__crud__sincronizacao__title">{{ title }}</label>
			<label
				v-if="ultimaIntegracao"
				class="container__crud__sincronizacao__subtitle"
				>Última sincronização em
				{{
					new Date(ultimaIntegracao).toLocaleDateString('pt-BR', {
						hour: 'numeric',
						minute: 'numeric',
						second: 'numeric',
					})
				}}</label
			>
			<label
				class="container__crud__sincronizacao__link"
				@click="arrayToCSV(dadosParaDownload, filename)"
				>{{ downloadLogs }}</label
			>
			<Button
				v-if="dadosParaSeremSincronizados.length"
				style="margin-top: 2.5rem"
				label="Sincronizar"
				color="trv-default"
				lefticon="fas fa-sync-alt"
				@click="showModal = true"
			/>
		</div>
		<Modal
			v-if="showModal"
			class="!top-0"
		>
			<div
				class="bg-white w-fit flex-col items-center min-w-[20%] p-10 relative"
				style="display: flex"
			>
				<div
					class="absolute top-2 right-2 cursor-pointer"
					@click="showModal = false"
				>
					<Icon
						v-show="!syncing && !confirm"
						icon="ic:round-close"
					/>
				</div>
				<div
					v-show="!syncing && confirm"
					class="flex flex-col items-center gap-8"
				>
					{{ $t('syncData') }}?
					<div class="flex gap-6">
						<Button
							outlined
							:label="$t('cancel')"
							@click="showModal = false"
						/>
						<Button
							:label="$t('sync')"
							@click="syncData"
						/>
					</div>
				</div>
				<div
					v-show="syncing"
					class="items-center flex flex-col gap-4"
				>
					<Spinner color="#95bb49" />
					{{ $t('syncingData') }}
				</div>
				<div
					v-show="!syncing && !confirm"
					class="flex flex-col items-center gap-8 relative"
				>
					<Icon
						width="30"
						color="#138600"
						icon="ic:round-check"
					/>
					{{ $t('syncCompleted') }}
				</div>
			</div>
		</Modal>
		<Button
			v-if="supportMode && !dadosParaSeremSincronizados.length"
			class="absolute w-fit"
			:label="$t('clear')"
			@click="limparDados"
		/>
	</div>
</template>

<script setup>
import { Icon } from '@iconify/vue2'
import { useLocalStorage } from '@vueuse/core'
import { computed, onMounted, ref, toRefs } from 'vue'
import Button from '../../../components/Button/Button'
import Modal from '../../../components/Modal/Modal'
import Spinner from '../../../components/Spinner/Spinner'
import {
	buscarInformacoesDeIntegracao,
	buscarPlantiosIntegrados,
	limparDados,
	sincronizarDados,
} from '../../../services/requests/IntegracaoRequests'
import { arrayToCSV } from '../../../utils/helpers'

const supportMode = useLocalStorage('suportMode', false)

const props = defineProps({
	urlIntegracao: {
		type: String,
		require: true,
	},
	tituloPagina: {
		type: String,
		require: true,
	},
})

const { tituloPagina } = toRefs(props)

const emit = defineEmits(['atualizarTitulo'])

const dadosParaSeremSincronizados = ref([])
const syncing = ref(false)
const confirm = ref(true)
const showModal = ref(false)
const ultimaIntegracao = ref(null)
const plantiosNaoIntegrados = ref([])
const dadosSincronizados = ref([])

const title = computed(() => {
	return dadosParaSeremSincronizados.value.length
		? 'Existem dados não sincronizados'
		: 'Todos os dados estão sincronizados'
})

const filename = computed(() => {
	return plantiosNaoIntegrados.value.length
		? 'Plantios não integrados'
		: 'Plantios integrados em ' +
				new Date(ultimaIntegracao.value).toLocaleDateString(navigator.language, {
					day: 'numeric',
					year: 'numeric',
					hour: 'numeric',
					minute: 'numeric',
					month: 'short',
				})
})

const downloadLogs = computed(() => {
	return dadosParaSeremSincronizados.value.length
		? 'Baixar log de dados não sincronizados'
		: 'Baixar log da última sincronização'
})

const dadosParaDownload = computed(() => {
	return dadosParaSeremSincronizados.value.length
		? plantiosNaoIntegrados.value
		: dadosSincronizados.value
})

const buscaInformacoes = () => {
	buscarInformacoesDeIntegracao().then((response) => {
		const { dataUltimaIntegracao, idsNaoIntegrados, plantiosAindaNaoIntegrados } = response.data
		dadosParaSeremSincronizados.value = idsNaoIntegrados
		ultimaIntegracao.value = dataUltimaIntegracao
		plantiosNaoIntegrados.value = plantiosAindaNaoIntegrados
	})

	buscarPlantiosIntegrados().then((response) => {
		dadosSincronizados.value = response.data
	})
}

const syncData = () => {
	syncing.value = true
	confirm.value = false
	sincronizarDados(dadosParaSeremSincronizados.value).then(() => {
		syncing.value = false
		buscaInformacoes()
	})
}

onMounted(() => {
	emit('atualizarTitulo', tituloPagina.value)
	buscaInformacoes()
})
</script>

<style scoped src="../../../css/crud.css"></style>

<style scoped>
.container__crud {
	display: flex;
	justify-content: center;
	min-height: 32.81rem;
}

.container__crud__sincronizacao {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	max-width: 360px;
}

.container__crud__sincronizacao__icon {
	margin-bottom: 0.5rem;
}

.container__crud__sincronizacao__title {
	font-size: 1.125rem;
	color: #0c0b0b;
	font-weight: 700;
	line-height: 1.53rem;
	margin-bottom: 0.5rem;
}

.container__crud__sincronizacao__subtitle {
	font-weight: 400;
	font-size: 1rem;
	line-height: 1.36rem;
	color: #4b4b4d;
	margin-bottom: 0.5rem;
}

.container__crud__sincronizacao__link {
	color: #2b6388;
	font-size: 0.875rem;
	text-decoration-line: underline;
	margin-bottom: 0.5rem;
	cursor: pointer;
}

.container__crudform__backdrop__visible {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: rgba(0, 0, 0, 0.2);
	z-index: 1100;
	display: flex;
	justify-content: center;
	align-items: center;
}

.container__crudform__backdrop__visible__dialog {
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 20px;
	min-width: 328px;
	min-height: 176px;
	background-color: white;
	border-radius: 5px;
}

.container__crudform__backdrop__visible__dialog_1 {
	display: flex;
	flex-direction: column;
	width: 100%;
	align-items: center;
	border: 1px solid red;
}

.container__crudform__backdrop__visible__dialog_1__buttons {
	width: 100%;
	display: flex;
	margin-top: 0.5rem;
	justify-content: space-between;
}
</style>
